import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import styled, { type CSSProperties } from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StepIcon } from '@sorare/core/src/atoms/icons/StepIcon';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { isType, withFragments } from '@sorare/core/src/lib/gql';

import { Lifes } from '#src/components/series/SeriesBoard/Lifes/index.tsx';

import { PickerOption } from './PickerCard.tsx';
import type {
  BoardPickerOption_careerBoard,
  careerProSteps_careerBoard,
} from './__generated__/BoardPickerOption.graphql.ts';

const Root = styled(PickerOption)`
  aspect-ratio: 1;
  padding: var(--triple-unit) calc(5 * var(--unit)) var(--triple-unit);
  overflow: hidden;

  &.engaged {
    background: linear-gradient(var(--c-nl-800), var(--c-nl-600))
      var(--board-color, var(--c-nd-100));
    padding: var(--double-unit) calc(5 * var(--unit)) var(--quadruple-unit);
  }
`;

const Bottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--unit) var(--half-unit);
  background: conic-gradient(
      from 90deg at 3px 3px,
      var(--c-nd-50) 25%,
      transparent 0
    )
    0 0/5px 5px;
`;

const TopRightSlot = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
  width: calc(var(--double-unit));
  height: calc(var(--double-unit));
`;

const TopLeftSlot = styled(Vertical).attrs({ gap: 0, center: true })`
  position: absolute;
  top: var(--unit);
  left: var(--unit);
`;

const StepsWrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  --step-overlap: calc(0.75 * var(--unit));
  transform: rotate(180deg);
  padding-top: var(--step-overlap);
  z-index: revert;
  & > * {
    margin-top: calc(-1 * var(--step-overlap));
  }
`;

const GridCardImage = styled.img`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  object-fit: contain;
`;

export const careerProSteps = withFragments(
  <T extends careerProSteps_careerBoard>(
    board: T
  ): Extract<T['steps'][number], { __typename: 'CareerProStep' }>[] =>
    board.steps.filter(
      (
        s
      ): s is Extract<T['steps'][number], { __typename: 'CareerProStep' }> => {
        if (!isType(s, 'CareerProStep')) {
          return false;
        }
        return !!s.myTask?.id;
      }
    ),
  {
    careerBoard: gql`
      fragment careerProSteps_careerBoard on CareerBoard {
        id
        steps {
          id
          ... on CareerProStep {
            id
            myTask {
              id
              aasmState
            }
          }
        }
      }
    ` as TypedDocumentNode<careerProSteps_careerBoard>,
  }
);

const hasClaimableTask = (board: careerProSteps_careerBoard) =>
  careerProSteps(board).some(
    s => s.myTask.aasmState === ManagerProgressionTaskState.COMPLETED
  );

type Props = {
  board: BoardPickerOption_careerBoard;
  selected: boolean;
  onClick: () => void;
};

export const BoardPickerOption = ({ board, selected, onClick }: Props) => {
  const { engaged, iconUrl, color } = board;
  return (
    <Root
      className={classNames({ selected, engaged })}
      style={{ '--board-color': color } as CSSProperties}
      onClick={onClick}
    >
      <TopLeftSlot>
        <StepsWrapper>
          {careerProSteps(board).map(step => (
            <StepIcon key={step.id} status={step.myTask.aasmState} size={1.5} />
          ))}
        </StepsWrapper>
      </TopLeftSlot>
      {!!engaged && (
        <TopRightSlot>
          {hasClaimableTask(board) ? (
            <span style={{ color: 'var(--c-green-400)' }}>
              <FontAwesomeIcon icon={iconGift} size="xs" />
            </span>
          ) : undefined}
        </TopRightSlot>
      )}
      {!!iconUrl && <GridCardImage src={iconUrl} alt={board.title ?? ''} />}
      {!!engaged && (
        <Bottom>
          <Horizontal gap={0.5} center>
            {careerProSteps(board).map(step => {
              const { aasmState } = step.myTask;
              const { myTask } = step;
              const isReady = aasmState === ManagerProgressionTaskState.READY;
              if (isReady && isType(myTask, 'ThresholdPickerTask')) {
                return (
                  <Horizontal key={step.id} gap={0.5} center>
                    <Lifes task={myTask} size={1.5} />
                  </Horizontal>
                );
              }
              return null;
            })}
          </Horizontal>
        </Bottom>
      )}
    </Root>
  );
};

BoardPickerOption.fragments = {
  careerBoard: gql`
    fragment BoardPickerOption_careerBoard on CareerBoard {
      id
      title
      iconUrl
      engaged
      color(variant: PRIMARY)
      steps {
        id
        ... on CareerProStep {
          id
          myTask {
            id
            ... on ThresholdPickerTask {
              id
              aasmState
              ...Lifes_thresholdPickerTask
            }
          }
        }
      }
      ...careerProSteps_careerBoard
    }
    ${careerProSteps.fragments.careerBoard}
    ${Lifes.fragments.thresholdPickerTask}
  ` as TypedDocumentNode<BoardPickerOption_careerBoard>,
};
